// NTP-style clock offset estimation between host (time master) and guest.
//
//   guest t0 ──ping──▶ host t1
//   guest t3 ◀─pong─── host t2
//   offset = ((t1 - t0) + (t2 - t3)) / 2   (host clock - guest clock)
//   rtt    = (t3 - t0) - (t2 - t1)
//
// The error of a single sample is bounded by rtt/2, so we keep the samples with
// the lowest RTT and take the median of their offsets.

export interface ClockSample {
  offset: number;
  rtt: number;
  at: number;
}

export function sampleFrom(t0: number, t1: number, t2: number, t3: number, at = t3): ClockSample {
  return {
    offset: ((t1 - t0) + (t2 - t3)) / 2,
    rtt: Math.max(0, (t3 - t0) - (t2 - t1)),
    at,
  };
}

export class ClockSync {
  private samples: ClockSample[] = [];

  constructor(private readonly keep = 24, private readonly best = 5) {}

  add(s: ClockSample) {
    this.samples.push(s);
    if (this.samples.length > this.keep) this.samples.shift();
  }

  get count() {
    return this.samples.length;
  }

  /** Estimated (host - local) offset in ms, or 0 before any sample. */
  get offset(): number {
    const b = this.bestSamples();
    if (!b.length) return 0;
    const offs = b.map((s) => s.offset).sort((a, c) => a - c);
    const m = offs.length >> 1;
    return offs.length % 2 ? offs[m] : (offs[m - 1] + offs[m]) / 2;
  }

  /** Upper bound on the offset error (half the best RTT). */
  get errorBound(): number {
    const b = this.bestSamples();
    return b.length ? b[0].rtt / 2 : Infinity;
  }

  get rtt(): number {
    const b = this.bestSamples();
    return b.length ? b[0].rtt : NaN;
  }

  hostToLocal(hostTime: number) {
    return hostTime - this.offset;
  }

  localToHost(localTime: number) {
    return localTime + this.offset;
  }

  private bestSamples() {
    return [...this.samples].sort((a, b) => a.rtt - b.rtt).slice(0, this.best);
  }
}

/**
 * Fires `fn` as close as possible to `targetMs` (performance.now() timebase):
 * a coarse setTimeout first, then a short spin for the last few ms, because
 * timers on mobile can fire 4-16 ms late. Callers should keep the main thread
 * free around the target (see ShootController) so the coarse timer is not delayed.
 */
export function fireAt(targetMs: number, fn: (lateByMs: number) => void, spinMs = 25): () => void {
  let cancelled = false;
  const spin = () => {
    if (cancelled) return;
    while (performance.now() < targetMs) { /* busy wait, at most spinMs */ }
    fn(performance.now() - targetMs);
  };
  const wait = targetMs - performance.now() - spinMs;
  const id = setTimeout(spin, Math.max(0, wait));
  return () => { cancelled = true; clearTimeout(id); };
}
